/**
 * =====================================================
 * SATCHMO'S BAR & GRILL - SITE CONFIGURATION
 * =====================================================
 * 
 * Hey team! This file controls what shows up on the website.
 * Just edit the text below and save the file - changes go live automatically.
 * 
 * TIPS:
 * - Keep text inside the "quotes"
 * - Don't delete any commas or brackets
 * - Prices should include the $ sign
 * - For bands, use format: "Wed, Jan 22" for dates
 * 
 * Questions? Call the web team or just copy an existing entry as a template!
 * =====================================================
 */

// =====================================================
// HAPPY HOUR SPECIALS
// Shows in the "Specials" tab on the menu
// =====================================================
export const happyHourSpecials = {
  // When is happy hour?
  title: "HAPPY HOUR SPECIALS",
  time: "2-6pm",

  // Individual deals shown as cards
  deals: [
    { item: "House Wine", price: "$4", icon: "wine" },
    { item: "Off Drafts", price: "$2", icon: "beer" },
    { item: "Wells", price: "$3", icon: "cocktail" },
    { item: "Off All Apps", price: "20%", icon: "food" },
  ],

  // Sunday special callout
  sunday: {
    label: "ALL DAY SUNDAY",
    description: "All Day Happy Hour Pricing",
  },
};

// =====================================================
// KITCHEN MENU
// Shows in the "Kitchen" tab on the menu
// =====================================================
export const kitchenMenu = {
  title: "FROM THE KITCHEN",
  
  items: [
    {
      name: "Smash Burgers",
      price: "From $12",
      description: "Double-stacked, crispy edges",
      highlight: true,
    },
    {
      name: "Loaded Fries",
      price: "$10",
      description: "Cheese, bacon, jalapeños, ranch",
      highlight: false,
    },
    {
      name: "Fish & Chips",
      price: "$14",
      description: "Beer-battered with house slaw",
      highlight: false,
    },
    {
      name: "Wings (Bone-In)",
      price: "From $13",
      description: "6 sauces to choose from",
      highlight: false,
    },
    {
      name: "Loaded Nachos",
      price: "$10",
      description: "Piled high with all the fixings",
      highlight: false,
    },
  ],
};

// =====================================================
// WEEKLY SPECIALS
// One special per day — shows on the Weekly Specials board
// and powers the "TONIGHT" chip in the hero.
//
// To update: just change the text. badge is an optional
// extra callout shown next to the special (leave it off
// for days without one).
// =====================================================
export const weeklySpecials = {
  title: "WEEKLY SPECIALS",
  subtitle: "Seven days, seven reasons to stop in",
  days: [
    { day: "Monday",    special: "Sizzling Chicken & Steak Fajitas" },
    { day: "Tuesday",   special: "Steakhouse Salad" },
    { day: "Wednesday", special: "Turkey Melt", badge: "Kids Eat FREE" },
    { day: "Thursday",  special: "Philly Cheesesteak", badge: "Free Trivia 7PM" },
    { day: "Friday",    special: "Golden Fish & Chips" },
    { day: "Saturday",  special: "Happy Hour 2–6PM", badge: "Live Music (most Saturdays)" },
    { day: "Sunday",    special: "All-Day Happy Hour" },
  ],
};

// =====================================================
// TRIVIA NIGHT (recurring, every week)
// The standing Thursday trivia night — shown as a feature
// card under the Weekly Specials board.
// (One-off trivia events from Facebook still use the
// triviaEvents list below for the confetti banner.)
// =====================================================
export const triviaNight = {
  enabled: true,
  day: "Thursday",
  time: "7:00 PM – 8:30 PM",
  headline: "TRIVIA THURSDAYS",
  tagline: "Get ready to show what you know!",
  details: "Free to play • Prizes every round • Hosted by Unleashed Entertainment",
};

// =====================================================
// LIVE MUSIC / UPCOMING BANDS
// Shows in the Live Music section
// 
// To add a new band:
// 1. Copy one of the entries below
// 2. Paste it at the TOP of the list (newest first)
// 3. Update the info
// 4. The first band in the list will be featured as "UP NEXT"
// 5. Add eventLink if you have a Facebook event or ticket page
// =====================================================
export const upcomingBands = [
  // ↓↓↓ FIRST BAND = "UP NEXT" FEATURED EVENT ↓↓↓
  {
    band: "Perry Woods",
    genre: "",
    date: "Sat, Aug 8",
    time: "6:00PM",
    description: "",
    eventLink: null,
  },
  {
    band: "Bob Miller",
    genre: "Classic Rock & Country",
    date: "Sat, Sep 12",
    time: "6:00PM",
    description: "",
    eventLink: null,
  },
  {
    band: "Bob Miller",
    genre: "Classic Rock & Country",
    date: "Sat, Sep 26",
    time: "6:00PM",
    description: "",
    eventLink: null,
  },
  // Add more bands above this line ↑↑↑
];

// =====================================================
// TRIVIA NIGHTS
// Shows as a celebratory confetti banner across the top
// of the site during the week leading up to each trivia night.
// Trivia events are NOT shown in the band lineup above.
//
// To add a trivia night:
// 1. Copy an entry below
// 2. Use the same date format as bands: "Thu, Jun 4"
// 3. time is when trivia starts, e.g. "9:00PM"
// 4. The banner appears automatically the week of the event
//    and disappears 9 seconds after the page loads.
// =====================================================
export const triviaEvents = [
  // Add more trivia nights above this line ↑↑↑
];

// =====================================================
// RESTAURANT INFO
// Basic info that appears throughout the site
// =====================================================
export const restaurantInfo = {
  name: "Satchmo's Bar & Grill",
  phone: "(314) 392-9401",
  address: "13375 Olive Blvd",
  city: "Chesterfield",
  state: "MO",
  zip: "63017",
  plaza: "Woodchase Shopping Plaza",
  established: "2013",

  // Google Maps driving directions link
  directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=13375+Olive+Blvd+Chesterfield+MO+63017",
  
  // Social media links (just update the URLs)
  social: {
    facebook: "https://www.facebook.com/satchmosstl",
    instagram: "https://www.instagram.com/satchmosbarandgrill/",
  },
  
  // Online ordering link (Toast) - update when ready
  orderOnlineUrl: "https://order.toasttab.com/online/satchmos-bar-and-grill-13375-olive-boulevard",
  
  // Menu links - update these when you have PDFs or pages ready
  fullMenuUrl: "https://order.toasttab.com/online/satchmos-bar-and-grill-13375-olive-boulevard",
  drinkSpecialsUrl: "/menus/cocktailsofthemonth.pdf",
  fullMenuPdfUrl: "/menus/satchmos-menu.pdf",
};

// =====================================================
// HOURS OF OPERATION
// =====================================================
export const hoursOfOperation = [
  { day: "Sunday", time: "11am – 9pm", closed: false, special: "All-Day Happy Hour" },
  { day: "Monday", time: "11am – 9pm", closed: false },
  { day: "Tuesday", time: "11am – 9pm", closed: false },
  { day: "Wednesday", time: "11am – 9pm", closed: false, special: "Kids Eat FREE" },
  { day: "Thursday", time: "11am – 9pm", closed: false, special: "Free Trivia 7PM" },
  { day: "Friday", time: "11am – 10pm", closed: false },
  { day: "Saturday", time: "11am – 10pm", closed: false, special: "Live Music (most)" },
];

// =====================================================
// HERO GALLERY
// Images that rotate in the hero section gallery.
// This reel sells the ATMOSPHERE — the room, the bar,
// the patio — plus one appetite shot. Food photography
// lives in the FOOD GALLERY collage below.
//
// To add a new image:
// 1. Drop the photo in frontend/public/gallery/ (or use a URL)
// 2. Add a new entry with src (image URL) and alt (description)
// 3. Images will auto-rotate based on the interval setting
// =====================================================
export const heroGallery = {
  // How long each image shows (in milliseconds) - 5000 = 5 seconds
  autoAdvanceInterval: 5000,

  // Gallery images - add as many as you want
  images: [
    {
      src: "https://customer-assets.emergentagent.com/job_elegant-satchmos/artifacts/bc9q72tk_cropped-MyStock-Satchmos-Bar-Grill-Front-scaled-1.jpg",
      alt: "Satchmo's Bar & Grill Exterior"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_50579129-1607-4b06-a1fd-cfa0320f9730/artifacts/hrguoc1v_image.png",
      alt: "Satchmo's Bar & Grill Interior"
    },
    {
      src: "/gallery/bar-interior.jpg",
      alt: "The bar at Satchmo's with high-top seating and big-screen TVs"
    },
    {
      src: "/gallery/dining-room-interior.jpg",
      alt: "Satchmo's dining room with exposed brick walls, big-screen TVs, and high-top tables"
    },
    {
      src: "/gallery/beer-taps.jpg",
      alt: "The draft beer tap wall behind the bar at Satchmo's"
    },
    {
      src: "/gallery/cheeseburger-fries.jpg",
      alt: "The Big Mo cheeseburger with lettuce and tomato, served with fries and a cold beer"
    },
    {
      src: "/gallery/buffalo-wings.jpg",
      alt: "Satchmo's buffalo wings with a side of ranch"
    },
    {
      src: "/gallery/philly-cheesesteak.jpg",
      alt: "Philly cheesesteak and fries on Satchmo's branded table"
    },
  ],
};

// =====================================================
// FOOD GALLERY ("From the Kitchen" collage)
// The food photography collage that appears right after
// the menu section, with a click-to-enlarge lightbox.
//
// To add a photo:
// 1. Drop the photo in frontend/public/gallery/ (or use a URL)
// 2. Copy an entry below — caption is the dish name shown
//    on hover and in the lightbox; alt describes the photo.
// 3. The first photo is featured larger in the collage.
// =====================================================
export const foodGallery = {
  title: "FROM THE KITCHEN",
  subtitle: "A Taste of Satchmo's",

  images: [
    // ↓↓↓ FIRST PHOTO = BIG FEATURED TILE ↓↓↓
    {
      src: "/gallery/cheeseburger-fries.jpg",
      caption: "The Big Mo with Fries",
      alt: "The Big Mo cheeseburger with lettuce and tomato, served with fries and a cold beer"
    },
    {
      src: "/gallery/buffalo-wings.jpg",
      caption: "Satchmo's Buffalo Wings",
      alt: "Satchmo's buffalo wings with a side of ranch"
    },
    {
      src: "/gallery/philly-cheesesteak.jpg",
      caption: "Philly Cheesesteak",
      alt: "Philly cheesesteak on a hoagie roll with a pile of fries"
    },
    {
      src: "/gallery/soft-pretzel.jpg",
      caption: "Bavarian Pretzel",
      alt: "Giant soft pretzel served with beer cheese and honey mustard dips"
    },
    {
      src: "/gallery/monte-cristo.jpg",
      caption: "Monte Cristo with Side Salad",
      alt: "Powdered-sugar dusted Monte Cristo sandwich with raspberry jam and a side salad"
    },
    {
      src: "/gallery/club-sandwich.jpg",
      caption: "Burger Club with Tots",
      alt: "Burger club sandwich with a side of tater tots"
    },
    {
      src: "/gallery/cobb-salad.jpg",
      caption: "Cobb Salad",
      alt: "Cobb salad with grilled chicken, bacon, avocado, egg, tomato, and blue cheese"
    },
    {
      src: "/gallery/grilled-salmon.jpg",
      caption: "Grilled Salmon",
      alt: "Grilled salmon fillet with lemon, steamed broccoli, and mashed potatoes"
    },
    {
      src: "/gallery/smothered-burrito.jpg",
      caption: "Smothered Burrito",
      alt: "Smothered burrito topped with melted cheese and jalapeños, served with guacamole, sour cream, and pico de gallo"
    },
    {
      src: "/gallery/patty-melt-onion-rings.jpg",
      caption: "Patty Melt & Onion Rings",
      alt: "Patty melt on rye with a side of golden onion rings"
    },
    {
      src: "/gallery/fried-chicken-sandwich.jpg",
      caption: "Spicy Chicken Sandwich",
      alt: "Spicy chicken sandwich with a cup of tomato soup"
    },
    // Add more photos above this line ↑↑↑
  ],
};

// =====================================================
// CORNER BANNER
// Promotional banner that appears in the top right corner
// 
// To update:
// - Set enabled to true/false to show/hide the banner
// - Update the image URL when you have a new promotion
// - Link goes to wherever you want people to click
// =====================================================
export const cornerBanner = {
  enabled: false,  // Set to false to hide the banner
  image: "https://imglink.cc/cdn/U8Gsphfs6i.png",
  alt: "Kentucky Derby Watch Party",
  link: "https://www.facebook.com/events/3180825408972142/",  // Links to Facebook event
};
